import React, { ChangeEvent, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import { DashValidation } from 'dash-common';
import {
  Flex,
  Form,
  FormContext,
  Input,
  Text,
  Textarea,
  AvatarUpload,
  useScreenResolution,
  useServerResponseTranslation,
  toast,
} from 'dash-ui';
import { useUploadDraftFileMutation } from 'gql/generated.graphql';
import { FormFields } from 'pages/authentication/complete-sign-up-scope/AddAdditionalInfoPage/component/AddAdditionalInfoForm/constants/constants';
import useValidation from 'pages/authentication/complete-sign-up-scope/AddAdditionalInfoPage/component/AddAdditionalInfoForm/hooks/useValidation';

const DELAY = 300;

// Used on post signup screen.
// For logged users, use AccountInfoForm.tsx
function ProfileCard() {
  const { t } = useTranslation(['forms', 'forms', 'pages']);
  const { formState, formErrors, stateHandlers } = useContext(FormContext);
  const validateFieldByName = useValidation();
  const theme = useTheme();
  const {
    devices: { isMobile },
  } = useScreenResolution();
  const translateErrorMessage = useServerResponseTranslation();
  const [uploadDraftFileMutation, { loading: isFileUploading }] =
    useUploadDraftFileMutation();

  const onTextChanged =
    (type: FormFields) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;

      stateHandlers[type](value);
      validateFieldByName(type, value);
    };
  const onNameChange = useDebouncedCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      stateHandlers[FormFields.AgencyName](e.target.value);
      validateFieldByName(FormFields.AgencyName, e.target.value);
    },
    DELAY
  );

  const onFileChange = (file: File | null) => {
    if (file) {
      uploadDraftFileMutation({ variables: { input: { file } } })
        .then(({ data }) => {
          if (data) {
            stateHandlers[FormFields.Avatar](data.uploadDraftFile);
          }
        })
        .catch((error) => toast.error(translateErrorMessage(error)));
    }
  };

  return (
    <Flex flexDirection="column" w="100%">
      <Flex w="100%">
        <Text bold xl>
          {t`pages:completeSignUpAddAdditionalInfoPage:profileSubHeader`}
        </Text>
      </Flex>
      <Flex flexDirection="column" w="100%" mt={3}>
        <Flex
          w="100%"
          mr={[0, 4, 4, 4]}
          flex={1}
          flexDirection={isMobile ? 'column' : 'row'}
        >
          <AvatarUpload
            url={formState.avatar?.publicUrl ?? ''}
            isLoading={isFileUploading}
            onChange={onFileChange}
            small={isMobile}
            medium={!isMobile}
          />

          <Flex
            ml={[0, 6, 6, 6]}
            mt={[4, 0, 0, 0]}
            flexDirection="column"
            w="100%"
          >
            <Form.Group w="100%">
              <Form.Label>{t`forms:labels:agencyName`}</Form.Label>
              <Input
                w="100%"
                onChange={onNameChange}
                $errorType={formErrors[FormFields.AgencyName]?.type}
              />
              <Form.Feedback
                $errorType={formErrors[FormFields.AgencyName]?.type}
              >
                {formErrors[FormFields.AgencyName]?.message}
              </Form.Feedback>
            </Form.Group>
            <Form.Group w="100%" mt={4}>
              <Form.Label>{t`forms:labels:agencyWebsite`}</Form.Label>
              <Input
                w="100%"
                placeholder={t`forms:placeholders:url` || ''}
                onChange={onTextChanged(FormFields.AgencyWebsite)}
                $errorType={formErrors[FormFields.AgencyWebsite]?.type}
              />
              <Form.Feedback
                $errorType={formErrors[FormFields.AgencyWebsite]?.type}
              >
                {formErrors[FormFields.AgencyWebsite]?.message}
              </Form.Feedback>
            </Form.Group>
          </Flex>
        </Flex>
        <Form.Group w="100%" mt={4}>
          <Flex w="100%" justifyContent="space-between">
            <Flex flexGrow={1}>
              <Form.Label>{t`labels.shortDescription`}</Form.Label>
            </Flex>
            <Flex>
              <Form.Label color={theme.colors.n70}>
                {formState.shortDescription?.length || 0}/
                {DashValidation.MaxDescriptionLength}
              </Form.Label>
            </Flex>
          </Flex>
          <Textarea
            w="100%"
            onChange={onTextChanged(FormFields.ShortDescription)}
            $errorType={formErrors[FormFields.ShortDescription]?.type}
            minHeight={150}
          />
          <Form.Feedback
            $errorType={formErrors[FormFields.ShortDescription]?.type}
          >
            {formErrors[FormFields.ShortDescription]?.message}
          </Form.Feedback>
        </Form.Group>
      </Flex>
    </Flex>
  );
}

export default ProfileCard;
